'use client';

import { useNavigate } from 'react-router-dom';
import ProductQuantityIncrement from '../ProductQuantityIncrement';
import { useEffect, useRef, useState } from 'react';
import LoadingButton from '../LoadingButton';
import CustomModal from '../CustomModal';
import { Tooltip } from '../Tooltip';
import { type ProductCardsProps, OutOfStock } from '../../interface/interface';
import OutOfStockOverlay from '../OutOfStockOverlay';
import { AllowedProducts } from '../AllowedProducts';
import { useGuestCart } from '../../context/guestCartContext';

const GuestProductCards = (props: ProductCardsProps) => {
  const {
    product,
    addProductToCart,
    removeProductFromCart,
    substitueCard = false,
    allowedProduct = false,
  } = props;
  const { getProductQuantity, refreshCart } = useGuestCart();

  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [openSubstituteModal, setSubstituteModal] = useState<boolean>(false);
  const [suggestAccessoriesModal, setSuggestAccessories] =
    useState<boolean>(false);
  const [addProductModalLoading, setAddProductModalLoading] =
    useState<boolean>(false);

  const [showTooltip, setShowTooltip] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const productNameRef = useRef<HTMLParagraphElement>(null);

  // Get current quantity from cart context
  const orderedQuantity = getProductQuantity(product.product_code);

  const handleAddToCart = async (
    productCode: string,
    substituteForId?: string,
  ) => {
    setLoading(true);
    await addProductToCart(productCode, substituteForId);
    await refreshCart(); // Refresh cart context after adding
    setLoading(false);
  };

  const handleRemoveFromCart = async (productCode: string) => {
    setLoading(true);
    await removeProductFromCart(productCode);
    await refreshCart(); // Refresh cart context after removing
    setLoading(false);
  };

  useEffect(() => {
    const checkOverflow = () => {
      if (productNameRef.current) {
        const isOverflowing =
          productNameRef.current.scrollWidth >
            productNameRef.current.clientWidth ||
          productNameRef.current.scrollHeight >
            productNameRef.current.clientHeight;
        setIsOverflowing(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, []);

  return (
    <li
      className="col-span-1 flex flex-col rounded-lg border border-secondary-darkred-50 
      border-solid bg-white text-center shadow px-5 py-4 cursor-pointer relative h-full"
      key={product.id}
    >
      <div
        className="flex flex-1 flex-col"
        onClick={() =>
          navigate(`/guest/product/${product.product_code}`, {
            state: {
              from: window.location.pathname,
            },
          })
        }
      >
        <div className="flex justify-between text-sm 3xl:text-base text-left font-bold text-secondary-cornflowerblue-600 capitalize">
          <p className="mr-2">{product.product_brand}</p>
          <p>${product.price}</p>
        </div>
        <div className="flex justify-between text-xs font-semibold text-grey-500 mb-1 uppercase items-center">
          <p
            className="text-left two-line-ellipsis relative"
            ref={productNameRef}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {product.name}
          </p>
          {isOverflowing && showTooltip && (
            <Tooltip
              text={product.name}
              className="z-50 product-name-tooltip"
            />
          )}
          <p>
            {product.weight}
            {product.unit}
          </p>
        </div>
        <div className="h-44 w-full flex justify-center relative">
          <img
            className="rounded-lg h-44"
            src={product.image_url || '/placeholder.svg'}
            alt="product-image"
            style={{ objectFit: 'contain' }}
            id={`product-image-${product.product_code}`}
          />
          <OutOfStockOverlay
            outOfStock={product.outOfStock}
            inventoryQuantity={product.inventory_quantity}
            isGuest={true}
          />
        </div>
      </div>
      <div>
        {orderedQuantity > 0 ? (
          <ProductQuantityIncrement
            productCode={product.product_code}
            orderedQuantity={orderedQuantity}
            productOutOfStock={product.outOfStock}
            maxQuantity={product.productMaxQty}
            addProductToCart={handleAddToCart}
            removeProductFromCart={handleRemoveFromCart}
            minimalist={false}
          />
        ) : (
          <LoadingButton
            buttonText="Add to Cart"
            isLoading={loading}
            buttonIcon={<>{'🛒'}</>}
            isDisabled={product.outOfStock !== OutOfStock.false}
            onClick={() => handleAddToCart(product.product_code)}
          />
        )}

        {allowedProduct && product.allowedProducts && (
          <AllowedProducts
            product={product}
            addProductToCart={handleAddToCart}
            removeProductFromCart={handleRemoveFromCart}
          />
        )}

        {!substitueCard &&
          product.substituteProduct &&
          !product.substitutions_not_available && (
            <div className="absolute bottom-4 left-0 w-full">
              <button
                className="w-11/12 bg-secondary-cornflowerblue-600 border border-secondary-cornflowerblue-600 py-3 
              rounded-md text-white text-sm font-semibold 
              hover:text-secondary-cornflowerblue-600 hover:bg-white"
                onClick={() => setSubstituteModal(true)}
              >
                Order substitution
              </button>
              <CustomModal
                isOpen={openSubstituteModal}
                onClose={() => setSubstituteModal(false)}
                header={''}
                footer={''}
                modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4 3xl:w-1/5"
              >
                <div>
                  <p className="mb-3 text-sm font-regular text-primary-blue-500 text-left">
                    Substitute product for:{' '}
                    <span className="font-semibold">{product.name}</span>
                  </p>
                  <GuestProductCards
                    product={product.substituteProduct}
                    addProductToCart={(id: string) =>
                      handleAddToCart(id, product.product_code)
                    }
                    removeProductFromCart={handleRemoveFromCart}
                    substitueCard={true}
                    isAccessories={false}
                    allowedProduct={false}
                  />
                </div>
              </CustomModal>
            </div>
          )}

        <div className="absolute text-start bottom-4 left-0 w-full z-40">
          <CustomModal
            isOpen={suggestAccessoriesModal}
            onClose={async () => {
              setLoading(true);
              setSuggestAccessories(false);
              await handleAddToCart(product.product_code);
              setLoading(false);
            }}
            header={'Dried Herb Product Added!'}
            footer={
              <>
                <LoadingButton
                  isLoading={addProductModalLoading}
                  className="!text-base !px-6 !py-3 !w-auto !rounded-md !mr-2"
                  onClick={async () => {
                    setAddProductModalLoading(true);
                    await handleAddToCart(product.product_code);
                    setAddProductModalLoading(false);
                    navigate('/guest/nonscript');
                  }}
                  buttonText="Proceed"
                />
                <button
                  className="text-base px-6 py-3 rounded-md !border !border-primary-blue-500"
                  onClick={async () => {
                    setSuggestAccessories(false);
                    setLoading(true);
                    await handleAddToCart(product.product_code);
                    setLoading(false);
                  }}
                >
                  Stay on this page
                </button>
              </>
            }
            modalSize="w-11/12 sm-land:w-3/4 md:w-3/5 lg:w-2/5 2xl:w-1/4"
          >
            <div className="text-start">
              Would you like to purchase required accessories along with this ?
            </div>
          </CustomModal>
        </div>
      </div>
    </li>
  );
};

export default GuestProductCards;
